import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useCurrentUser, useUserActions } from 'stores/useUserStore';

import type { Locale } from 'utils/intlUtils';

import DatadogLogger from 'components/core/logging/DatadogLogger';
import LoggingService from 'components/core/logging/LoggingService';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useRouter } from 'hooks/useRouter';
import { useWindowUnloadEffect } from 'hooks/useWindowUnloadEffect';
import { createLaunchDarklyContext } from 'utils/featureFlagUtils';
import { setLocale } from 'utils/intlUtils';
import { localeQueryOverrideStorage } from 'utils/storage/intl';

/**
 * Bootstraps the Zustand user store from React-sourced values and owns user-related
 * lifecycle side effects. Wires the LD client, feature flags, and router redirect
 * callback into the user store, and drives the LD identify, locale, session-replay,
 * and window-unload effects. State lives in the store; consumers read it through
 * the atomic selector hooks (`useCurrentUser`, `useUserActions`, `useHasPermissions`, …).
 * `useUser()` is a transitional aggregator retained only for the builder provider.
 *
 * Must be called once, high in the tree, below the LaunchDarkly and Router providers
 * (see `UserStoreInitializer`, the mount shell that hosts it in the provider stack).
 */
export const useInitializeUserStore = () => {
  const ldClient = useLDClient();
  const { flags: featureFlags } = useFeatureFlags();
  const { redirectBackToSectionPath } = useRouter();
  const user = useCurrentUser();
  const { setFeatureFlags, setRedirectFn } = useUserActions();

  useEffect(() => {
    setFeatureFlags(featureFlags);
    // setFeatureFlags is a stable store action; featureFlags is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags]);

  useEffect(() => {
    setRedirectFn(redirectBackToSectionPath);
    // setRedirectFn is a stable store action; redirectBackToSectionPath is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectBackToSectionPath]);

  /*
   * First-identify race fix: setUser() runs from RouterRoot's mount effect, and the LD client
   * from useLDClient() resolves asynchronously — either can land first. Without this catch-up
   * identify, a session whose user arrives before the client would start with an anonymous LD context.
   */
  useEffect(() => {
    if (!ldClient || !user.id) {
      return;
    }

    void ldClient.identify(createLaunchDarklyContext(user)).catch(error => {
      LoggingService.error({ error, message: 'Failed to identify LaunchDarkly user.' });
    });
    // user.id dep: fires identify on ID change only, not on every user property update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ldClient, user.id]);

  useEffect(() => {
    if (user.locale?.languageTag && !localeQueryOverrideStorage.get()) {
      setLocale(user.locale.languageTag as Locale);
    }
  }, [user.locale?.languageTag]);

  /*
   * callOnCleanup=false: only fire on real window unload, not on React unmounts.
   * Intentional divergence from the original UserProvider (which passed `true`): passing `true`
   * killed the LD client on every Strict Mode double-mount and HMR cycle, leaving the app
   * with a closed LD client on refresh. `false` preserves the intended session-boundary
   * semantics while staying safe in development.
   */
  useWindowUnloadEffect(() => {
    DatadogLogger.stopSessionReplayRecording();

    void ldClient?.close();
  }, false);

  useEffect(() => {
    /**
     * To save costs we enable session replay on an as needed basis, based on `userId`.
     * Should always be false unless we need to debug an issue further.
     */
    const isNotAnonymousUser = !!user?.id;
    if (isNotAnonymousUser && featureFlags.sessionReplayEnabled === true) {
      DatadogLogger.startSessionReplayRecording();
    } else {
      DatadogLogger.stopSessionReplayRecording();
    }
  }, [featureFlags, user?.id]);
};

interface UserStoreInitializerProps {
  /**
   * Component subtree that receives the initialized user store.
   */
  children: ReactNode;
}

export const UserStoreInitializer = ({ children }: UserStoreInitializerProps) => {
  useInitializeUserStore();

  return <>{children}</>;
};
